import {
  DISPLAY_WINDOW_NAME,
  MESSAGE,
  VIEW,
  createSignal,
  createStateMessage,
  isRatableRatingSession,
  parseMessage
} from './protocol';

const DISPLAY_TIMEOUT_MS = 6000;
const STATUS_CHECK_MS = 1000;
const WINDOW_FEATURES = 'popup=yes,width=1280,height=720,left=80,top=60,resizable=yes,scrollbars=no';

// Finds a screen other than the one this tab is on via the Window Management
// API (Chromium only). The first call shows Chrome's one-time permission
// prompt, so it must run while this tab is still in front: ask BEFORE opening
// the popup, otherwise the prompt is hidden behind the newly focused window.
// Returns null when unsupported, denied, or there is only one screen.
async function findSecondScreen(w) {
  if (typeof w.getScreenDetails !== 'function') return null;
  try {
    const details = await w.getScreenDetails();
    const current = details.currentScreen;
    return (
      details.screens.find(
        (screen) => screen !== current && !(screen.left === current.left && screen.top === current.top)
      ) || null
    );
  } catch (error) {
    return null;
  }
}

// What the UI should tell the user before opening the display:
//   'prompt'        - Chrome has not asked yet; warn them to click Allow
//   'denied'        - they blocked it earlier; explain how to re-enable
//   'granted'       - nothing to say
//   'single-screen' - only one (or mirrored) screen, placement is pointless
//   'unsupported'   - browser has no Window Management API
async function readScreenPlacementState(w) {
  if (typeof w.getScreenDetails !== 'function') return 'unsupported';
  if (w.screen && w.screen.isExtended === false) return 'single-screen';
  const permissions = w.navigator && w.navigator.permissions;
  if (!permissions || typeof permissions.query !== 'function') return 'unsupported';
  // Chrome renamed the permission from 'window-placement' to 'window-management'.
  for (const name of ['window-management', 'window-placement']) {
    try {
      return (await permissions.query({ name })).state;
    } catch (error) {
      // Try the next name.
    }
  }
  return 'unsupported';
}

function featuresFor(screen) {
  if (!screen) return WINDOW_FEATURES;
  return `popup=yes,left=${screen.availLeft},top=${screen.availTop},width=${screen.availWidth},height=${screen.availHeight},resizable=yes,scrollbars=no`;
}

function randomId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// One publisher per browser tab (module singleton on the Vue 2 side). It owns
// the tab's "what should the customer see" view and decides when it may send
// it:
//  - a tab publishes only while it is visible; becoming visible/focused claims
//    its current document with a fresh epoch, which the display ranks above
//    older tabs. Payment completion keeps its original epoch and cannot claim
//    ownership again merely because its old tab is focused;
//  - a hidden/stale tab keeps its latest view locally but never sends, so it
//    cannot overwrite the active tab or clear THANK YOU;
//  - the display asks (HELLO) after a refresh/reopen and the active tab
//    replays its current view.
// No Vue, Vuex, or document-form knowledge lives here.
export function createDisplayPublisher({ transport, displayUrl, win, doc, now, tabId }) {
  const w = win || window;
  const d = doc || w.document;
  const clock = now || Date.now;
  const id = tabId || randomId();

  let view = null; // last desired view of this tab: sanitized on send
  let viewOwnerKey = null; // who set it (component key), for release()
  let settlement = null; // Pay page's payment summary, merged onto `view` on send
  // Evaluation Session state stays inside the cashier-side publisher and is
  // associated with the original Quotation/Invoice id. The document id is
  // deliberately never copied into the customer-display wire protocol.
  let currentDocumentId = null;
  let ratingSession = null;
  let ratingSessionResolved = false;
  let epoch = null;
  let seq = 0;
  let displayHandle = null;
  let opening = null; // in-flight openDisplay() while the permission prompt is up
  let lastSeen = 0;
  let online = false;
  let latestOtherActiveEpoch = -Infinity;
  const statusListeners = [];

  const isActive = () => d.visibilityState !== 'hidden';

  function send() {
    if (!view || !isActive()) return;
    if (epoch === null) epoch = clock();
    seq += 1;
    let outgoing = view;
    if (view.kind === VIEW.ACTIVE) {
      outgoing = Object.assign({}, view, { ratingSession });
    }
    if (settlement) outgoing = Object.assign({}, outgoing, { settlement });
    transport.post(createStateMessage({ sender: id, epoch, seq, view: outgoing }));
  }

  function claim() {
    // THANK YOU belongs to the payment event, not to subsequent focus events.
    // A display refresh may replay it with its original epoch, so a newer
    // document still wins regardless of message arrival order.
    if (!view || view.kind === VIEW.THANK_YOU || view.kind === VIEW.RATING) return;
    epoch = clock();
    send();
  }

  function refreshStatus(next) {
    if (next === online) return;
    online = next;
    statusListeners.slice().forEach((fn) => fn(online));
  }

  const unsubscribe = transport.subscribe((raw) => {
    const message = parseMessage(raw);
    if (!message) return;
    if (message.type === MESSAGE.STATE) {
      // Once a newer document is published, this payment completion is over.
      // Retiring it also prevents a later display refresh from resurrecting it
      // if this paid tab happens to be the only visible tab answering HELLO.
      if (message.sender !== id && message.view.kind === VIEW.ACTIVE) {
        latestOtherActiveEpoch = Math.max(latestOtherActiveEpoch, message.epoch);
        if (view && (view.kind === VIEW.THANK_YOU || view.kind === VIEW.RATING) && message.epoch > epoch) {
          view = null;
          settlement = null;
          currentDocumentId = null;
          ratingSession = null;
          ratingSessionResolved = false;
        }
      }
    } else if (message.type === MESSAGE.ALIVE) {
      lastSeen = clock();
      refreshStatus(true);
    } else if (message.type === MESSAGE.BYE) {
      lastSeen = 0;
      displayHandle = null;
      refreshStatus(false);
    } else if (message.type === MESSAGE.HELLO) {
      lastSeen = clock();
      refreshStatus(true);
      send();
    } else if (message.type === MESSAGE.RATING_COMPLETED) {
      // The display submits rating directly to the backend and never relays
      // it through this tab - this is only the small completion fact, so a
      // later HELLO replay (display refresh/reopen) does not resend stale,
      // already-rated controls. Ignored if it no longer matches what this
      // tab currently believes is the live session (a new document already
      // took over, or this tab never owned that session to begin with).
      const ownsSession = Boolean(ratingSession) && message.sessionId === ratingSession.id;
      const ownsView = Boolean(view && view.kind === VIEW.RATING && view.ratingSession && view.ratingSession.id === message.sessionId);
      if (ownsSession) ratingSession = Object.assign({}, ratingSession, { status: 'rated', rating: message.rating });
      if (ownsView) view = { kind: VIEW.THANK_YOU, branch: view.branch, ratingSession, settlement: view.settlement };
      if (ownsSession || ownsView) send();
    } else if (message.type === MESSAGE.RATING_FLOW_DONE) {
      // The post-rating screen's own timer elapsed and the display went back
      // to idle on its own - mirror that here too, so a refresh/reopen does
      // not replay the finished flow (or its settlement) from the start.
      const matches =
        (ratingSession && message.sessionId === ratingSession.id) ||
        (view && view.ratingSession && view.ratingSession.id === message.sessionId);
      if (matches) {
        view = { kind: VIEW.IDLE, branch: view && view.branch };
        viewOwnerKey = null;
        settlement = null;
        ratingSession = null;
        ratingSessionResolved = false;
        send();
      }
    } else if (message.type === MESSAGE.RATING_UNAVAILABLE) {
      // The pre-payment inline session expired or was rejected as invalid,
      // discovered by the display's own live attempt or its own expiry
      // watch. Unlike RATING_FLOW_DONE, the document itself must stay
      // visible (the quotation/invoice is still open, just no longer
      // ratable) - only the matching session is marked terminal, via an
      // existing allow-listed status, so a replay never resends dead
      // controls. Ignored if it no longer matches the live session (a new
      // document already took over).
      if (ratingSession && message.sessionId === ratingSession.id) {
        ratingSession = Object.assign({}, ratingSession, { status: 'timeout' });
        send();
      }
    }
  });

  const onVisibility = () => {
    if (isActive()) claim();
  };
  d.addEventListener('visibilitychange', onVisibility);
  w.addEventListener('focus', claim);
  const statusTimer = w.setInterval(() => {
    if (online && clock() - lastSeen > DISPLAY_TIMEOUT_MS) refreshStatus(false);
  }, STATUS_CHECK_MS);

  return {
    tabId: id,

    // Set what this tab wants the customer to see. `ownerKey` identifies the
    // caller so only that caller can later release() it. A fresh document
    // view drops any settlement left over from a previous payment.
    setView(nextView, ownerKey, context) {
      const nextDocumentId = context && context.documentId ? String(context.documentId) : null;
      if (nextDocumentId !== currentDocumentId) {
        currentDocumentId = nextDocumentId;
        ratingSession = null;
        ratingSessionResolved = false;
      }
      view = nextView;
      viewOwnerKey = ownerKey === undefined ? null : ownerKey;
      settlement = null;
      send();
    },

    // Result of evaluation_session/get_or_create for the current original
    // Quotation/Invoice. A late response for a document that no longer owns
    // this publisher is ignored rather than attached to the next customer.
    setRatingSession(documentId, nextRatingSession) {
      if (!documentId || String(documentId) !== currentDocumentId) return false;
      ratingSession = nextRatingSession || null;
      ratingSessionResolved = true;
      send();
      return true;
    },

    hasRatingSessionState(documentId) {
      return Boolean(documentId) && String(documentId) === currentDocumentId && ratingSessionResolved;
    },

    // Pay pages can be opened directly (without their Detail page first).
    // Associate the original document internally so a freshly fetched session
    // can still be used; this does not publish the document id or any view.
    associateDocument(documentId) {
      const nextDocumentId = documentId ? String(documentId) : null;
      if (nextDocumentId === currentDocumentId) return;
      currentDocumentId = nextDocumentId;
      ratingSession = null;
      ratingSessionResolved = false;
    },

    // What the Pay page is keying in right now (channel, amount received,
    // change/owed), or null once it no longer applies. Does not touch `view`
    // or its ownership - it rides along on whatever is already being sent,
    // including into the THANK YOU that follows a successful payment.
    setSettlement(nextSettlement) {
      settlement = nextSettlement;
      send();
    },

    // Payment succeeded for this tab's currently owned document. Keep that
    // ownership epoch: a late payment response from a tab already superseded
    // by another document must not claim the display again. HELLO may replay
    // the completion with the same epoch after a display refresh.
    showThankYou(branch) {
      if (latestOtherActiveEpoch > (epoch === null ? -Infinity : epoch)) {
        view = null;
        viewOwnerKey = null;
        settlement = null;
        currentDocumentId = null;
        ratingSession = null;
        ratingSessionResolved = false;
        return;
      }
      view = { kind: VIEW.THANK_YOU, branch };
      viewOwnerKey = null;
      send();
    },

    // Payment completion uses the session belonging to the ORIGINAL document,
    // never a Receipt/Invoice id returned by pay_document. A waiting session
    // within its window becomes the interactive RATING state. A session
    // already rated before payment keeps the established THANK_YOU kind but
    // carries the session along, so the display shows the branch's configured
    // post-rating content (with its own timer) instead of the plain message -
    // "rated before payment" must never show the interactive full-screen
    // prompt again (see Phase 4 requirement #9). Closed/timeout/cancelled/
    // unknown/absent/mismatched sessions keep the plain THANK_YOU untouched.
    paymentSucceeded(branch, originalDocumentId) {
      if (latestOtherActiveEpoch > (epoch === null ? -Infinity : epoch)) {
        view = null;
        viewOwnerKey = null;
        settlement = null;
        currentDocumentId = null;
        ratingSession = null;
        ratingSessionResolved = false;
        return;
      }
      const matches = originalDocumentId && String(originalDocumentId) === currentDocumentId;
      const session = matches ? ratingSession : null;
      if (session && isRatableRatingSession(session, clock())) {
        view = { kind: VIEW.RATING, branch, ratingSession: session };
      } else {
        view = { kind: VIEW.THANK_YOU, branch, ratingSession: session && session.status === 'rated' ? session : null };
      }
      viewOwnerKey = null;
      send();
    },

    // The caller's document view went away (page closed/left). Returns the
    // display to idle, but never clears a THANK YOU and never touches a view
    // another caller has since set.
    release(ownerKey, branch) {
      if (!view || view.kind !== VIEW.ACTIVE || viewOwnerKey !== ownerKey) return;
      view = { kind: VIEW.IDLE, branch };
      viewOwnerKey = null;
      settlement = null;
      send();
    },

    isDisplayOnline: () => online,

    onDisplayStatus(fn) {
      statusListeners.push(fn);
      return () => statusListeners.splice(statusListeners.indexOf(fn), 1);
    },

    // Ask before openDisplay() so the UI can prepare the user for Chrome's
    // permission prompt. 'not-needed' when a display is already open/live,
    // because openDisplay() will only focus it and never prompt.
    async screenPlacementState() {
      if ((displayHandle && !displayHandle.closed) || online) return 'not-needed';
      return readScreenPlacementState(w);
    },

    // Must be called from a user gesture (click). Reuses/focuses an existing
    // display instead of creating duplicates. Resolves to:
    //   'focused'      - this tab's own handle was still open
    //   'already-open' - a display exists (opened elsewhere); asked it to focus
    //   'opened'       - a new window was created (on the second screen when the
    //                    browser allows it, otherwise at the default position)
    //   'blocked'      - the browser blocked the popup
    async openDisplay() {
      if (displayHandle && !displayHandle.closed) {
        try {
          displayHandle.focus();
        } catch (error) {
          // Ignore; the window still exists.
        }
        return 'focused';
      }
      displayHandle = null;
      if (online) {
        transport.post(createSignal(MESSAGE.FOCUS));
        return 'already-open';
      }
      // A second click while the permission prompt is showing joins the first.
      if (!opening) {
        opening = (async () => {
          const screen = await findSecondScreen(w);
          const opened = w.open(displayUrl, DISPLAY_WINDOW_NAME, featuresFor(screen));
          if (!opened) return 'blocked';
          displayHandle = opened;
          try {
            opened.focus();
          } catch (error) {
            // Ignore.
          }
          return 'opened';
        })().finally(() => {
          opening = null;
        });
      }
      return opening;
    },

    // Closes the display wherever it was opened: directly when this tab owns the
    // handle, and by asking the window to close itself (covers a display opened
    // from another tab or after this tab reloaded).
    closeDisplay() {
      if (displayHandle && !displayHandle.closed) {
        try {
          displayHandle.close();
        } catch (error) {
          // Fall through to the signal below.
        }
      }
      displayHandle = null;
      transport.post(createSignal(MESSAGE.CLOSE));
    },

    destroy() {
      unsubscribe();
      d.removeEventListener('visibilitychange', onVisibility);
      w.removeEventListener('focus', claim);
      w.clearInterval(statusTimer);
    }
  };
}
